const loadGoogleMapsApi = require('load-google-maps-api');
import MarkerClusterer from './Locator-1-Markercluster';

/*
  The GoogleMaps class serves as a layer on top of the official Google Maps API and provides helper methods for centering,
  and adding locations to the map.
  This class is not meant to be instantiated directly through the ComponentRepository, but rather by another class like Map.
  The GoogleMaps class expects the locations to be an instance of either MapLocation or a subclass thereof,
  but it is not strictly required.

  ARGS:
  -googleMapsAPIKey: string API key for the Google Maps API
  -locations: array of MapLocation instances
  -defaultMarkerImage: object that specifies the default marker used for locations, if the location itself does not override it. Ex: { url: '/Static/assets/images/marker.png', width: 50, height: 70}
  -gMapsOptions: object with google maps options as specified in the documentation: https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions
*/
export default class GoogleMaps {
    constructor(elm, args) {
        args.gMapsOptions = args.gMapsOptions || {};
        args.gMapsOptions.zoom = args.gMapsOptions.zoom || 10;
        args.defaultMarkerImage = args.defaultMarkerImage || { url: null, width: 0, height: 0 };
        this.breakpoint = window.matchMedia('(min-width:992px)');

        const self = this;
        this.lastPan = 0;
        this.initializedPromiseResolve = null;
        this.initializedPromiseReject = null;
        this.initializedPromise = new Promise((resolve, reject) => {
            this.initializedPromiseResolve = resolve;
            this.initializedPromiseReject = reject;
        });

        self.updateListView = args.updateListView;
        this.defaultMarkerImage;
        this.clusterIcon;
        this.map;
        this.locations = [];
        this.infoWindow = null;
        this.markerCluster = null;
        this.inboundMarkers = [];

        this.startZoom = args.gMapsOptions.zoom;
        this.startLat = args.gMapsOptions.center.lat;
        this.startLng = args.gMapsOptions.center.lng;
        this.lastNodeidSum = 0;
        this.markers = [];
        self.setMarkersInbound = this.setMarkersInbound;
        const throttleListViewUpdate = throttle(() => self.setMarkersInbound(self), 1000);

        loadGoogleMapsApi({
            key: args.googleMapsAPIKey,
        })
            .then(function (googleMaps) {
                self.defaultMarkerImage = new google.maps.MarkerImage(args.defaultMarkerImage.url, null, null, null, new google.maps.Size(args.defaultMarkerImage.width, args.defaultMarkerImage.height));
                self.map = new googleMaps.Map(elm, args.gMapsOptions);

                self.map.addListener('bounds_changed', () => {
                    throttleListViewUpdate();
                });

                self.map.addListener('zoom_changed', () => {
                    throttleListViewUpdate();
                });

                self.infoWindow = new google.maps.InfoWindow({
                    pixelOffset: new google.maps.Size(0, 0),
                });
                self.initializedPromiseResolve();
            });

        // call public functions that await the initializedPromise before loadGoogleMapsApi resolves to have it queued up before any external invocations
        self.setLocations(args.locations, args);

        function throttle(func, timeFrame) {
            let lastTime = 0;
            return () => {
                const now = Date.now();
                if (now - lastTime >= timeFrame) {
                    func();
                    lastTime = now;
                }
            };
        }
    }
    // m.isAdded &&
    setMarkersInbound(self) {
        if (self && this.breakpoint.matches) {
            const bounds = self.map.getBounds();
            if (bounds) {
                const newInboundMarkers = self.markers.filter(m => bounds.contains(m.getPosition()));
                if (newInboundMarkers.length == 0) {
                    if (self.lastNodeidSum != 0) {
                        self.inboundMarkers = self.markers;
                        self.updateListView(self.markers);
                        self.lastNodeidSum = 0;
                    }
                } else if (self.lastNodeidSum != newInboundMarkers.reduce((a, b) => a + b.nodeId, 0)) {
                    self.inboundMarkers = newInboundMarkers;
                    self.updateListView(newInboundMarkers);
                    self.lastNodeidSum = newInboundMarkers.reduce((a, b) => a + b.nodeId, 0);
                }
            }
        }
    }

    /*
    Zooms in the map to contain all locations
    */
    fitBounds() {
        this.initializedPromise
            .then(() => {
                const bounds = new google.maps.LatLngBounds();
                for (let i = 0; i < this.locations.length; i++) {
                    bounds.extend(this.locations[i].getPosition());
                }

                this.map.fitBounds(bounds);
            });
    }

    /*
    Sets the center of the map to the specified coordinate.
    CENTER:
    -lat: float
    -lng: float
    */
    setCenter(center) {
        this.initializedPromise
            .then(() => {
                this.map.setCenter(new google.maps.LatLng(center.lat, center.lng));
            });
    }

    /*
    Sets the center of the map to the specified coordinate.
    CENTER:
    -lat: float
    -lng: float
    */
    panTo(location) {
        this.initializedPromise
            .then(() => {
                this.lastPan = Date.now();
                this.map.panTo(new google.maps.LatLng(location.lat, location.lng));
                this.map.setZoom(location.zoom);
            });
    }

    /*
    newLocations: array of MapLocation instances
    */
    setLocations(newLocations, args) {
        this.initializedPromise
            .then(() => {
                // clear all markers
                for (let i = 0; i < this.locations.length; i++) {
                    this.locations[i].setMap(null);
                }

                this.locations = [];
                const map = this.map;
                const infoWindow = this.infoWindow;
                this.markers = [];

                if (!newLocations) {
                    return;
                }

                // Add new markers
                for (let i = 0; i < newLocations.length; i++) {
                    if (newLocations[i].latitude != null && newLocations[i].longitude != null) {
                        const marker = new google.maps.Marker({
                            position: new google.maps.LatLng(newLocations[i].latitude, newLocations[i].longitude),
                            map: this.map,
                            nodeId: newLocations[i].nodeId,
                            icon: newLocations[i].pinIcon ? new google.maps.MarkerImage(newLocations[i].pinIcon.url, null, null, null, new google.maps.Size(newLocations[i].pinIcon.width, newLocations[i].pinIcon.height)) : this.defaultMarkerImage,
                        });

                        function onMapClick(newLocation, infoWindow) {
                            if (newLocation.onMapClick) {
                                newLocation.onMapClick(infoWindow, event);
                            } else {
                                infoWindow.close();
                            }
                        }

                        function onMarkerClick(marker, map, newLocation, infoWindow) {
                            const infoWindowContent = newLocation.getInfoWindowContent ? newLocation.getInfoWindowContent : null;
                            if (newLocation.onMarkerClick) {
                                newLocation.onMarkerClick(infoWindow, marker, map, event);
                            } else {
                                infoWindowContent(newLocation).then(content => {
                                    infoWindow.pixelOffset.width = 0;
                                    infoWindow.pixelOffset.height = 0;
                                    infoWindow.setContent(content);
                                    infoWindow.open(map, marker);
                                });
                            }
                        }

                        map.addListener('click', () => {
                            onMapClick(newLocations[i], infoWindow);
                        });

                        marker.addListener('click', () => {
                            onMarkerClick(marker, map, newLocations[i], infoWindow);
                        });

                        this.markers.push(marker);
                        this.locations.push(marker);
                    }
                }
                const markerOptions = {
                    styles: [{
                        textColor: '#222222',
                        textSize: 20,
                        url: args.map.googleMaps.clusterIcon.url,
                        width: 35,
                        height: 35,
                    }],
                };

                if (this.markerCluster) {
                    this.markerCluster.clearMarkers();
                    this.markerCluster.addMarkers(this.markers, false);
                } else {
                    this.markerCluster = new MarkerClusterer(map, this.markers, markerOptions);
                }

                this.setMarkersInbound(this);
                if (this.lastNodeidSum == 0 && newLocations.length > 0) {
                    if (this.markerCluster && this.markerCluster.map) {
                        this.panTo({ zoom: this.startZoom, lat: this.startLat, lng: this.startLng });
                    }
                }
            });
    }
}
